import type { ShadowField } from "../native/shadow";
import { originalIcon } from "./native-original-icons";
import { setNativeIcon } from "./native-icons";

/** A third row of tools; only the selected value and vertical slider cover the canvas. */
export class NativeShadowCompact {
  readonly element = document.createElement("div");
  constructor(
    bind: (field: ShadowField, input: HTMLInputElement) => void,
    private finish: () => void,
  ) {
    const row = this.element;
    row.id = "native-shadow-compact";
    row.className = "native-property-row";
    row.hidden = true;
    for (const [field, title, icon, max] of [
      ["color", "Цвет тени", "color", 0],
      ["blur", "Размер тени (размытие)", "blur", 200],
      ["alpha", "Непрозрачность тени", "opacity", 100],
      ["distance", "Расстояние тени", "line-spacing", 1000],
      ["angle", "Направление тени", "motion", 360],
    ] as const) {
      const anchor = document.createElement("div"),
        trigger = document.createElement("button");
      anchor.className = "native-property-anchor";
      trigger.type = "button";
      trigger.title = trigger.ariaLabel = title;
      trigger.dataset.shadowField = field;
      trigger.innerHTML = originalIcon(icon);
      if (field === "alpha") setNativeIcon(trigger, "opacity");
      trigger.onmousedown = (e) => e.preventDefault();
      anchor.append(trigger);
      row.append(anchor);
      if (field === "color") {
        trigger.dataset.colorInput = "native-shadow-color";
        trigger.classList.add("native-color-button");
        trigger.onclick = () => this.dismissPopup();
        trigger.onkeydown = (e) => {
          if (e.key === "Enter" || e.key === " ") this.dismissPopup();
        };
        continue;
      }
      const popup = document.createElement("div");
      popup.className = "native-property-popup native-shadow-slider";
      popup.id = "compact-shadow-" + field;
      popup.hidden = true;
      trigger.setAttribute("aria-controls", popup.id);
      trigger.setAttribute("aria-expanded", "false");
      for (const type of ["number", "range"] as const) {
        const input = document.createElement("input");
        input.type = type;
        input.id = popup.id + "-" + type;
        input.min = "0";
        input.max = String(max);
        input.step = "1";
        input.setAttribute("aria-label", title);
        if (type === "range") input.className = "native-original-vertical";
        else input.placeholder = "Разные";
        popup.append(input);
        bind(field, input);
      }
      anchor.append(popup);
      trigger.onclick = () => {
        this.finish();
        const open = !!popup.hidden;
        this.dismissPopup();
        popup.hidden = !open;
        trigger.setAttribute("aria-expanded", String(open));
        trigger.classList.toggle("active", open);
      };
    }
    document.getElementById("native-context-properties")!.prepend(row);
    const outside = (e: Event) => {
      const target = e.target as HTMLElement;
      // The shared palette consumes pointer propagation before target handlers on iOS.
      if (
        (!row.contains(target) && !target.closest("#irl-color-picker")) ||
        (row.contains(target) && target.closest('[data-shadow-field="color"]'))
      )
        this.dismissPopup();
      if (
        !row.hidden &&
        target.closest("#native-context-properties,.native-toolbar") &&
        !row.contains(target) &&
        !target.closest('[data-original-control="show-shadow"]')
      )
        this.close();
    };
    document.addEventListener("pointerdown", outside, true);
    document.addEventListener("click", outside, true);
  }
  get opened() {
    return !this.element.hidden;
  }
  open() {
    this.element.hidden = false;
    this.expanded(true);
  }
  close() {
    this.dismissPopup();
    this.element.hidden = true;
    this.expanded(false);
  }
  private expanded(open: boolean) {
    document
      .querySelector('[data-original-control="show-shadow"]')
      ?.setAttribute("aria-expanded", String(open));
  }
  dismissPopup() {
    let closed = false;
    for (const popup of this.element.querySelectorAll<HTMLElement>(
      ".native-property-popup",
    )) {
      closed ||= !popup.hidden;
      popup.hidden = true;
    }
    for (const trigger of this.element.querySelectorAll("[aria-expanded]")) {
      trigger.setAttribute("aria-expanded", "false");
      trigger.classList.remove("active");
    }
    return closed;
  }
  color(value: string) {
    this.element
      .querySelector<HTMLElement>('[data-shadow-field="color"]')!
      .style.setProperty("--swatch", value);
  }
}
